import dayjs from "dayjs";

import { normalizeDrawingRotation, type DrawingPlane, type DrawingRotation, type SelectionCoords } from "@synthesis/pdf-viewer";
import type {
  ModelDrawingPlacementDtoStatusEnumKey,
  ModelsDrawingDto,
  ModelsDrawingWithPlacementsDto,
} from "@synthesis/shared-services/models/modelsDrawingsController/types";

import type { FileMetaDto } from "../../api";
import { DocumentsTypes } from "../../types";

export type { DrawingPlane };
export type DrawingKind = "чертёж" | "страница";
export type DrawingStatus = ModelDrawingPlacementDtoStatusEnumKey;

const DEFAULT_PLACEMENT_STATUS: DrawingStatus = "PARAMETERS_NOT_DEFINED";
const PROTECTED_PLACEMENT_STATUSES: DrawingStatus[] = ["READY_FOR_PLACEMENT", "PLACED"];

export type DrawingCollectionRow = {
  id: string;
  fileName: string;
  kind: DrawingKind;
  ifcBinding: string[];
  protectedModelIds: string[];
  plane?: DrawingPlane;
  expert: string;
  createdAtMs: number;
  date: string;
  time: string;
  pdfId: string;
  page: number;
  highlightArea?: SelectionCoords;
  rotation: DrawingRotation;
  status: DrawingStatus;
};

export type BindDrawingTarget = {
  id: string;
  name: string;
  modelIds: string[];
  protectedModelIds: string[];
};

export const DEFAULT_PDF_PAGE = 1;
export const EMPTY_PAGE_COUNT = 0;
export const MIN_DRAWINGS_FOR_ZIP = 2;
export const DRAWINGS_ZIP_FILE_NAME = "drawings.zip";

const DOWNLOAD_ERROR_TITLES = {
  singlePage: "Не удалось скачать страницу",
  archive: "Не удалось скачать архив чертежей",
  selected: "Не удалось скачать выбранные чертежи",
} as const;

export const getDownloadErrorTitle = (count: number) => {
  if (count === 1) {
    return DOWNLOAD_ERROR_TITLES.singlePage;
  }

  if (count >= MIN_DRAWINGS_FOR_ZIP) {
    return DOWNLOAD_ERROR_TITLES.archive;
  }

  return DOWNLOAD_ERROR_TITLES.selected;
};

export function getDrawingHighlightArea(dto: ModelsDrawingDto): SelectionCoords | undefined {
  const { originX, originY, areaWidth, areaHeight } = dto;
  if (originX == null || originY == null || areaWidth == null || areaHeight == null || areaWidth <= 0 || areaHeight <= 0) {
    return undefined;
  }

  return { originX, originY, areaWidth, areaHeight };
}

export function drawingToFileMeta(drawing: DrawingCollectionRow): FileMetaDto {
  return {
    id: drawing.id,
    author: drawing.expert,
    baseName: drawing.fileName,
    createdAt: drawing.date,
    modifiedAt: `${drawing.date} ${drawing.time}`,
    ext: "pdf",
    isDeleted: false,
    size: 0,
    sourceId: drawing.id,
    sourceType: "PROJECT_ID",
    type: DocumentsTypes.pdf_document,
    version: 1,
  };
}

type DrawingSearchNames = {
  userNamesById?: Record<string, string | null>;
  ifcNamesById?: Record<string, string | null>;
};

const normalizeSearchText = (value: string) => value.trim().toLocaleLowerCase("ru").replaceAll("ё", "е");

export function filterDrawings(rows: DrawingCollectionRow[], search: string, names: DrawingSearchNames = {}): DrawingCollectionRow[] {
  const query = normalizeSearchText(search);
  if (!query) return rows;

  const { userNamesById = {}, ifcNamesById = {} } = names;

  return rows.filter((row) => {
    const expertName = userNamesById[row.expert] || row.expert;
    const ifcLabels = row.ifcBinding.map((id) => ifcNamesById[id] || id);
    const fields = [row.fileName, row.kind, row.plane, row.date, row.time, expertName, ...ifcLabels];

    return fields.some((field) => field && normalizeSearchText(field).includes(query));
  });
}

export const mapModelsDrawingDtoToRow = (item: ModelsDrawingWithPlacementsDto): DrawingCollectionRow | null => {
  const dto = item.drawing;

  if (!dto) {
    return null;
  }

  const placements = item.placements ?? [];
  const ifcBinding = placements.map((placement) => placement.modelId).filter(Boolean);
  const protectedModelIds = placements
    .filter((placement) => PROTECTED_PLACEMENT_STATUSES.includes(placement.status))
    .map((placement) => placement.modelId);
  const status: DrawingStatus = placements.some((placement) => placement.status === "PLACED")
    ? "PLACED"
    : (placements[0]?.status ?? DEFAULT_PLACEMENT_STATUS);

  const plane: DrawingPlane | undefined = (() => {
    if (dto.planXY && dto.planXY.length > 0) {
      return "План XY";
    }

    if (dto.sectionYZ && dto.sectionYZ.length > 0) {
      return "Разрез YZ";
    }

    if (dto.sectionXY && dto.sectionXY.length > 0) {
      return "Разрез XZ";
    }

    return undefined;
  })();

  const createdAt = (() => {
    if (dto.createdAt == null || dto.createdAt === "") {
      return dayjs();
    }

    const asUnix = Number(dto.createdAt);

    if (Number.isFinite(asUnix) && /^\d+(\.\d+)?$/.test(String(dto.createdAt).trim())) {
      return dayjs.unix(asUnix);
    }

    return dayjs(dto.createdAt);
  })();

  return {
    id: dto.id ?? "",
    fileName: dto.name ?? "",
    kind: "чертёж",
    ifcBinding,
    protectedModelIds,
    plane,
    expert: dto.author ?? "",
    createdAtMs: createdAt.isValid() ? createdAt.valueOf() : 0,
    date: createdAt.format("DD.MM.YYYY"),
    time: createdAt.format("HH:mm:ss"),
    pdfId: dto.pdfId ?? "",
    page: dto.page ?? 1,
    highlightArea: getDrawingHighlightArea(dto),
    rotation: normalizeDrawingRotation(dto.rotation),
    status,
  };
};

export const mapDrawingList = (items: ModelsDrawingWithPlacementsDto[] | undefined): DrawingCollectionRow[] =>
  (items ?? []).map(mapModelsDrawingDtoToRow).filter((row): row is DrawingCollectionRow => row !== null);

export const buildSinglePageDownloads = (drawingsToDownload: DrawingCollectionRow[]) =>
  drawingsToDownload.map((drawing) => {
    const page = drawing.page || DEFAULT_PDF_PAGE;

    return {
      pdfId: drawing.pdfId,
      page,
      fileName: `${drawing.fileName}_page_${page}.pdf`,
    };
  });

export const getSelectedDrawings = (rows: DrawingCollectionRow[], selectedIds: readonly string[]): DrawingCollectionRow[] => {
  const ids = new Set(selectedIds);

  return rows.filter((row) => ids.has(row.id));
};

export const getDownloadableDrawings = (rows: DrawingCollectionRow[], selectedIds: readonly string[]): DrawingCollectionRow[] =>
  getSelectedDrawings(rows, selectedIds).filter((row) => row.pdfId);

export const findDrawingById = (rows: DrawingCollectionRow[], id: string | undefined): DrawingCollectionRow | undefined =>
  rows.find((row) => row.id === id);

export const toBindDrawingTargets = (rows: DrawingCollectionRow[]): BindDrawingTarget[] =>
  rows.map(({ id, fileName, ifcBinding, protectedModelIds }) => ({
    id,
    name: fileName,
    modelIds: ifcBinding,
    protectedModelIds,
  }));
